import React from 'react';

// Editorial full-bleed welcome screen. Auth flow unchanged — the three CTAs are
// preserved as props: onCreateAccount, onSignIn, onContinueAsGuest.
// Hero photo: drop a file at public/welcome-hero.jpg (portrait, ~1080x1920).
// Falls back to a warm dark canvas if the image is missing.

// Splash page keeps the elegant editorial serif (the prod look) — DM Sans is the
// app-wide font, but the welcome screen's three display lines (Son:mi, the tagline,
// "Continue as guest") read far nicer in Cormorant. Loaded in public/index.html.
const SERIF = 'var(--font-sonmi-display)';
const SANS = '"DM Sans", system-ui, sans-serif';
const HERO_SRC = (process.env.PUBLIC_URL || '') + '/welcome-hero.jpg';

// `returning` swaps which of the two buttons is the filled one. Email traffic
// from the climate send is all people who already have accounts and have not
// signed in for a month, so Sign in leads for them. Everything else is
// untouched, including the default first-visit order.
export default function WelcomeScreen({ onSignIn, onCreateAccount, onContinueAsGuest, returning = false, exploreFirst = false }) {
  const filled = {
    background: '#F7EFE7', color: '#241310', border: 'none',
  };
  const outlined = {
    background: 'rgba(255,255,255,0.10)', color: '#FFFFFF',
    border: '1px solid rgba(247,241,234,0.55)',
    WebkitBackdropFilter: 'blur(6px)', backdropFilter: 'blur(6px)',
  };
  return (
    <div style={{ position: 'relative', minHeight: '100dvh', width: '100%', overflow: 'hidden', background: '#241310', fontFamily: SANS }}>
      {/* Full-bleed hero image */}
      <img
        src={HERO_SRC}
        alt=""
        aria-hidden="true"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {/* Legibility gradient — darkens the top (wordmark) and bottom (CTAs) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, rgba(20,10,8,0.45) 0%, rgba(20,10,8,0) 26%, rgba(20,10,8,0) 42%, rgba(20,10,8,0.55) 72%, rgba(20,10,8,0.88) 100%)',
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          // Bottom padding clears the iOS home-indicator safe area so the last CTA
          // ("Continue as guest") is never clipped under it. Was a flat 30px, which
          // pushed the guest button off-screen on iPhones with a home indicator.
          padding: '22px 26px calc(30px + env(safe-area-inset-bottom, 20px))',
          maxWidth: 480,
          margin: '0 auto',
        }}
      >
        {/* Wordmark, top centre */}
        <div style={{ textAlign: 'center', paddingTop: 6 }}>
          <span
            style={{
              color: '#F4ECE3',
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontSize: 'clamp(56px, 14vw, 72px)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              textShadow: '0 2px 18px rgba(0,0,0,0.5)',
            }}
          >
            Son:mi
          </span>
        </div>

        {/* Tagline + CTAs, anchored to the bottom */}
        <div style={{ marginTop: 'auto' }}>
          <p
            style={{
              textAlign: 'center',
              color: '#F7EFE7',
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontSize: 27,
              lineHeight: 1.2,
              margin: '0 0 30px',
              textShadow: '0 1px 18px rgba(0,0,0,0.5)',
            }}
          >
            Skincare,
            <br />
            in conversation.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {exploreFirst ? (
              <>
                <button
                  type="button"
                  onClick={onContinueAsGuest}
                  style={{
                    ...filled,
                    borderRadius: 999,
                    padding: '17px 24px',
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    fontFamily: SANS,
                    cursor: 'pointer',
                  }}
                >
                  Continue as guest
                </button>
                <button
                  type="button"
                  onClick={onSignIn}
                  style={{
                    ...outlined,
                    borderRadius: 999,
                    padding: '16px 24px',
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    fontFamily: SANS,
                    cursor: 'pointer',
                  }}
                >
                  Already use Son:mi? Sign in
                </button>
              </>
            ) : (
              <>
            <button
              type="button"
              onClick={onCreateAccount}
              style={{
                background: '#F7F1EA',
                color: '#2A1212',
                border: 'none',
                borderRadius: 999,
                padding: '17px 24px',
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontFamily: SANS,
                cursor: 'pointer',
                ...(returning ? outlined : filled),
                order: returning ? 2 : 1,
              }}
            >
              Create your account
            </button>

            <button
              type="button"
              onClick={onSignIn}
              style={{
                background: 'rgba(255,255,255,0.10)',
                color: '#FFFFFF',
                border: '1px solid rgba(247,241,234,0.55)',
                borderRadius: 999,
                padding: '16px 24px',
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontFamily: SANS,
                cursor: 'pointer',
                WebkitBackdropFilter: 'blur(6px)',
                backdropFilter: 'blur(6px)',
                ...(returning ? filled : outlined),
                order: returning ? 1 : 2,
              }}
            >
              Sign in
            </button>

            <button
              type="button"
              onClick={onContinueAsGuest}
              style={{
                background: 'rgba(247,239,231,0.18)',
                color: '#F7EFE7',
                border: '1px solid rgba(247,241,234,0.72)',
                borderRadius: 999,
                padding: '14px 24px',
                fontSize: 18,
                fontFamily: SERIF,
                fontStyle: 'italic',
                cursor: 'pointer',
                opacity: 1,
                WebkitBackdropFilter: 'blur(6px)',
                backdropFilter: 'blur(6px)',
                textShadow: '0 1px 12px rgba(0,0,0,0.5)',
                order: 3,
              }}
            >
              Continue as guest
            </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
